import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Popup from 'devextreme-react/popup';
import { useApiConfiguration } from '../../../api/api.configuration';
import { useScreenSize } from '../../../utils/media-query';
import DataGrid, { Column, Editing, Lookup, Button as Butt, Toolbar, Item, SearchPanel, Paging, Pager, Export, Scrolling } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { useAppSelector } from '../../../contexts/hooks';
import { Button, FileUploader, Tooltip, Form } from 'devextreme-react';
import ScrollView from 'devextreme-react/scroll-view';
import { ButtonItem, Label, SimpleItem } from 'devextreme-react/form';
import { billingPrices, LoadTarifas } from '../../../images/icons';
import ImagenFormat from '../../../images/FormatTarifasExcel.png';
import * as XLSX from 'xlsx';
import { newToast, ModalConfirmation } from '../../../components';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';
import { exportDataGrid } from 'devextreme/excel_exporter';

type massiveChangetp = {
  Type: typeMassiveChange | null;
  Value: number;
};

enum typeMassiveChange {
  increment_percentage = 1,
  decrement_percentage = 2,
  increment_value = 3,
  decrement_value = 4,
}

export const ModalTarifas = ({ visible, setVisible, Id }: { visible: boolean; setVisible: (value: boolean) => void; Id: number }) => {
  const dataGridRef = useRef<DataGrid>(null);
  const frmMassiveRef = useRef<Form>(null);
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  const [massiveChange, setMassiveChange] = useState<massiveChangetp>({ Type: null, Value: 0 });
  const { GetTarifasByIdContrato, newTarifa, updateTarifa, deleteTarifa, deleteAllTarifasByContrato, newTarifaMasive, updateTarifaMasive } = useApiConfiguration();
  const estudios = useAppSelector((x) => x.configuration.Data?.Estudios) || [];
  const [loadFileVisible, setLoadFileVisible] = useState(false);
  const [modalMassiveChangeVisible, setModalMassiveChangeVisible] = useState(false);
  const [modalDeleteAllVisible, setModalDeleteAllVisible] = useState(false);
  const fileUploaderLabel = { 'aria-label': 'Cargar excel' };
  const fileExtensions = ['.csv', '.xlsx', '.xls'];
  const [masiveLoadExcel, setMasiveLoadExcel] = useState({ loading: false, steep: '', success: [], error: [] });
  useEffect(() => {
    if (visible) {
      setMasiveLoadExcel({ loading: false, steep: '', success: [], error: [] });
      setLoadFileVisible(false);
      setModalMassiveChangeVisible(false);
      // Reset massive change data
      frmMassiveRef.current?.instance?.resetValues();
      setMassiveChange({ Type: null, Value: 0 });
    }
  }, [visible]);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Tf_Id',
      load() {
        return GetTarifasByIdContrato(Id)
          .then((res) => ({
            data: res,
            totalCount: res.totalCount,
            summary: res.summary,
            groupCount: res.groupCount,
          }))
          .catch(() => {
            throw new Error('Error de carga de datos');
          });
      },
      insert: async (values) => {
        return newTarifa(values, Id)
          .then((res) => {
            if (res === 105) {
              dataGridRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error insertando el analito');
          });
      },
      update: (key, values) => {
        return updateTarifa(key, values)
          .then((res) => {
            if (res === 107) {
              dataGridRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error actualizando el analito');
          });
      },
      remove: (key) => {
        return deleteTarifa(key).then((res) => {
          if (res) {
            dataGridRef.current?.instance.refresh();
          }
        });
      },
    });
  }, [visible]);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  const handleVisible = useCallback(() => {
    setVisible(false);
  }, []);
  const handleUpdateData = useCallback(() => {
    dataGridRef?.current?.instance.refresh();
  }, []);
  const handleMassiveChange = useCallback(() => {
    // Verificar si hay tarifas en el grid
    GetTarifasByIdContrato(Id).then((result) => {
      if (!result || result.length === 0) {
        newToast('WARNING', 'No hay tarifas para modificar', 5);
        return;
      }
      setModalMassiveChangeVisible(true);
    });
  }, [setModalMassiveChangeVisible, Id, GetTarifasByIdContrato]);

  const onCancelModalMassiveChangeClick = useCallback(() => {
    setModalMassiveChangeVisible(false);
  }, [setModalMassiveChangeVisible]);

  const handleDeleteAllTarifas = useCallback(() => {
    // Verificar si hay tarifas en el grid
    GetTarifasByIdContrato(Id).then((result) => {
      if (!result || result.length === 0) {
        newToast('WARNING', 'No hay tarifas para eliminar', 5);
        return;
      }
      setModalDeleteAllVisible(true);
    });
  }, [Id, GetTarifasByIdContrato]);

  const handleDeleteAllConfirm = useCallback(async () => {
    setModalDeleteAllVisible(false);
    try {
      await dataGridRef.current?.instance.beginCustomLoading('Eliminando tarifas...');
      const result = await deleteAllTarifasByContrato(Id);
      if (result) {
        dataGridRef.current?.instance.refresh();
      }
    } catch (error) {
      console.error('Error al eliminar las tarifas:', error);
      newToast('ERROR', 'Error al eliminar las tarifas', 5);
    } finally {
      await dataGridRef.current?.instance.endCustomLoading();
    }
  }, [Id, deleteAllTarifasByContrato]);

  const handleDeleteAllCancel = useCallback(() => {
    setModalDeleteAllVisible(false);
  }, []);
  const handleMassiveChangeConfirm = useCallback(async () => {
    if (!massiveChange.Type || massiveChange.Value <= 0) {
      newToast('WARNING', 'Debe seleccionar un tipo de cambio y un valor válido', 5);
      return;
    }

    try {
      // Cargar las tarifas actuales
      const currentData = await GetTarifasByIdContrato(Id);

      if (!currentData || currentData.length === 0) {
        newToast('WARNING', 'No hay tarifas para modificar', 5);
        return;
      }

      // Preparar los datos para enviar al backend
      const dataToSend = {
        IdContrato: Id, // ID del contrato
        Type: massiveChange.Type, // Tipo de modificación (enum)
        Value: massiveChange.Value, // Valor de la modificación
      };

      updateTarifaMasive(dataToSend).then((res) => {
        if (res.length > 0) {
          dataGridRef.current?.instance.refresh();
        }
      });

      setModalMassiveChangeVisible(false);

      // Reset form
      frmMassiveRef.current?.instance?.resetValues();
      setMassiveChange({ Type: null, Value: 0 });

      newToast('SUCCESS', 'Cambios aplicados correctamente', 3);
    } catch (error) {
      console.error('Error al preparar los datos:', error);
      newToast('ERROR', 'Error al aplicar los cambios', 5);
    }
  }, [setModalMassiveChangeVisible, Id, GetTarifasByIdContrato, massiveChange, updateTarifaMasive]);

  const handleFileChange = useCallback((e) => {
    const file = e[0]; // Obtener el primer archivo seleccionado

    if (!file) return;
    setMasiveLoadExcel({ loading: true, steep: 'Cargando archivo...', success: [], error: [] });

    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'xlsx' && fileExtension !== 'xls' && fileExtension !== 'csv') {
      alert('Por favor seleccione un archivo Excel o CSV');
      setMasiveLoadExcel({ loading: false, steep: '', success: [], error: [] });
      return;
    }

    // Llamamos a la función de carga una vez que se haya seleccionado el archivo
    handleLoadExcel(file);
  }, []);

  const handleLoadExcel = useCallback((file: File): void => {
    // Asegurarse de que el tipo de archivo sea correcto
    const reader = new FileReader();
    setMasiveLoadExcel({ loading: true, steep: 'Procesando archivo...', success: [], error: [] });
    reader.onload = (e: ProgressEvent<FileReader>): void => {
      const data = e.target?.result;
      if (!data) return;

      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      // Procesar el archivo dependiendo de la extensión
      if (fileExtension === 'csv') {
        // Si es CSV, procesarlo
        const csvData = (data as string).split('\n').map((row) => row.split(','));
        processData(csvData); // Llamar la función que extrae los datos
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        // Si es un archivo Excel (XLSX o XLS)
        const workbook = XLSX.read(data as string, { type: 'binary' });
        const sheetName = workbook.SheetNames[0]; // Obtener la primera hoja
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);
        processData(jsonData); // Llamar la función que extrae los datos
      } else {
        newToast('ERROR', 'El archivo no tiene un formato válido', 5);
        setMasiveLoadExcel({ loading: false, steep: '', success: [], error: [] });
      }
    };

    // Leer el archivo dependiendo de su tipo
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (fileExtension === 'csv') {
      reader.readAsText(file); // Leer como texto si es CSV
    } else {
      reader.readAsBinaryString(file); // Leer como binario si es Excel
    }
  }, []);

  const processData = (data: any[]) => {
    setMasiveLoadExcel({ loading: true, steep: 'Procesando datos...', success: [], error: [] });

    // Obtener las claves originales y normalizadas para la comparación robusta
    const originalKeys = Object.keys(data[0]);
    const normalizedKeys = originalKeys.map((key) =>
      key
        .toLowerCase()
        .replace(/\s|\t|\n|\r/g, '')
        .trim()
    );

    // Función para buscar la clave original que contenga la palabra clave (robusto a espacios, tabs, etc)
    const findKey = (target: string) => {
      const idx = normalizedKeys.findIndex((k) => k.includes(target));
      return idx !== -1 ? originalKeys[idx] : null;
    };

    // Validar que existan las columnas requeridas
    const cupsKey = findKey('cups');
    const nombreKey = findKey('nombre');
    const tarifaKey = findKey('tarifa');
    if (!cupsKey || !nombreKey || !tarifaKey) {
      newToast('ERROR', 'El archivo no tiene las columnas requeridas: Cups, Nombre y Tarifa', 5);
      setMasiveLoadExcel({ loading: false, steep: '', success: [], error: [] });
      return;
    }

    // Extraer los datos de las columnas necesarias de cada fila
    const extractedData = data
      .map((row: any, i: number) => {
        // Buscar la clave real en cada fila (por si hay inconsistencias)
        const rowKeys = Object.keys(row);
        const rowNormKeys = rowKeys.map((key) =>
          key
            .toLowerCase()
            .replace(/\s|\t|\n|\r/g, '')
            .trim()
        );
        const cupsK = rowKeys[rowNormKeys.findIndex((k) => k.includes('cups'))];
        const nombreK = rowKeys[rowNormKeys.findIndex((k) => k.includes('nombre'))];
        const tarifaK = rowKeys[rowNormKeys.findIndex((k) => k.includes('tarifa'))];

        if (!cupsK || !nombreK || !tarifaK) {
          newToast('ERROR', `Algunas columnas requeridas no están presentes en el archivo: CUPS=${cupsK}, NOMBRE=${nombreK}, TARIFA=${tarifaK}, fila ${i + 2}`, 5);
          return null;
        }

        return {
          Codigo: row[cupsK],
          Nombre: row[nombreK],
          Tarifa: row[tarifaK],
        };
      })
      .filter((row) => row !== null);

    setMasiveLoadExcel({ loading: true, steep: 'Creando tarifas...', success: [], error: [] });

    newTarifaMasive(extractedData, Id).then((res) => {
      if (res) {
        setMasiveLoadExcel({ loading: false, steep: '', success: res.LstSuccess, error: res.LstError });
        dataGridRef.current?.instance.refresh();
      }
    });
  };

  const onExporting = useCallback((e) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Tarifas');

    // Customizar la exportación para separar CUPS y NOMBRE
    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }) => {
        // Personalizar encabezados
        if (gridCell && gridCell.rowType === 'header' && gridCell.column) {
          if (gridCell.column.dataField === 'Tf_Es_Id' && gridCell.column.caption === 'Estudio') {
            // Este encabezado será para CUPS, el siguiente para NOMBRE
            excelCell.value = 'CUPS';
          } else if (gridCell.column.dataField === 'Tf_Valor') {
            excelCell.value = 'TARIFA';
          }
        }
      },
    }).then(() => {
      // Procesar las filas de datos para separar CUPS y NOMBRE
      const headerRow = worksheet.getRow(1);

      // Encontrar las columnas
      let estudioColIndex = 0;
      let precioColIndex = 0;
      let idColIndex = 0;

      headerRow.eachCell((cell, colNumber) => {
        const cellValue = cell.value?.toString() || '';
        if (cellValue === 'Id') idColIndex = colNumber;
        if (cellValue === 'Estudio' || cellValue === 'CUPS') estudioColIndex = colNumber;
        if (cellValue === 'Precio' || cellValue === 'TARIFA' || cellValue === 'VALOR') precioColIndex = colNumber;
      });

      // Eliminar columna ID si existe
      if (idColIndex > 0) {
        worksheet.spliceColumns(idColIndex, 1);
        // Ajustar índices después de eliminar la columna
        if (estudioColIndex > idColIndex) estudioColIndex--;
        if (precioColIndex > idColIndex) precioColIndex--;
      }

      // Insertar columna NOMBRE después de CUPS solo si se encontró la columna Estudio
      if (estudioColIndex > 0) {
        worksheet.spliceColumns(estudioColIndex + 1, 0, []);

        // Ajustar el header
        worksheet.getRow(1).getCell(estudioColIndex).value = 'CUPS';
        worksheet.getRow(1).getCell(estudioColIndex + 1).value = 'NOMBRE';
        if (precioColIndex >= estudioColIndex) precioColIndex++;
        if (precioColIndex > 0) {
          worksheet.getRow(1).getCell(precioColIndex).value = 'TARIFA';
        }

        // Procesar cada fila de datos
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber > 1) {
            // Saltar el header
            const estudioCell = row.getCell(estudioColIndex);
            const estudioValue = estudioCell.value?.toString() || '';

            // Separar CUPS y NOMBRE (formato: "CUPS - NOMBRE")
            const parts = estudioValue.split(' - ');
            if (parts.length >= 2) {
              row.getCell(estudioColIndex).value = parts[0].trim(); // CUPS
              row.getCell(estudioColIndex + 1).value = parts.slice(1).join(' - ').trim(); // NOMBRE
            } else {
              row.getCell(estudioColIndex).value = estudioValue;
              row.getCell(estudioColIndex + 1).value = '';
            }
          }
        });
      }

      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Tarifas.xlsx');
      });
    });
  }, []);

  return (
    <div>
      <ModalConfirmation visible={modalDeleteAllVisible} buttons='No/Yes' onConfirm={handleDeleteAllConfirm} onCancel={handleDeleteAllCancel} message={'Está seguro que desea eliminar TODAS las tarifas de este contrato?\n\nEsta acción no se puede deshacer.'} />
      <Popup height={'auto'} maxHeight={'90%'} width={1000} maxWidth={'95%'} fullScreen={isMedium || isSmall || isXSmall} visible={visible} onHidden={handleVisible} title='Tarifas' showTitle={true} showCloseButton={true}>
        {loadFileVisible && (
          <ScrollView style={{ maxHeight: '90vh', paddingRight: 8 }}>
            <div className='fileuploader-container'>
              <h5 style={{ marginBottom: 3, paddingLeft: 5 }}>Seleccione un archivo de excel que cumpla con el siguiente formato</h5>
              <img src={ImagenFormat} alt='Formato de archivo' />
              <hr />
              <FileUploader inputAttr={fileUploaderLabel} selectButtonText='Seleccione' uploadButtonText='Analizar archivo' labelText='o arrastre el archivo de excel aquí' allowedFileExtensions={fileExtensions} uploadMode='useForm' onValueChange={handleFileChange} />
              {masiveLoadExcel.loading && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 120,
                    background: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: 12,
                    margin: '18px 0 8px 0',
                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.07)',
                    padding: 24,
                  }}>
                  <LoadIndicator id='small-indicator' height={40} width={40} />
                  <h4 style={{ marginTop: 14, color: '#1976d2', fontWeight: 600, letterSpacing: 0.5 }}>{masiveLoadExcel.steep}</h4>
                </div>
              )}
              {masiveLoadExcel.success.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h4>Se han creado las siguientes tarifas:</h4>
                  <ul style={{ paddingLeft: 20 }}>
                    {masiveLoadExcel.success.map((item, index) => (
                      <li key={index} style={{ color: 'green', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: 'green', marginRight: 6 }}></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {masiveLoadExcel.error.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h4>Se han producido errores al crear las siguientes tarifas:</h4>
                  <ul style={{ paddingLeft: 20 }}>
                    {masiveLoadExcel.error.map((item, index) => (
                      <li key={index} style={{ color: 'red', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: 'red', marginRight: 6 }}></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(masiveLoadExcel.error.length > 0 || masiveLoadExcel.success.length > 0) && (
                <Button
                  text='Cerrar'
                  onClick={() => {
                    setMasiveLoadExcel({ loading: false, steep: '', success: [], error: [] });
                    setLoadFileVisible(false);
                  }}
                  style={{ marginTop: 16 }}
                />
              )}
            </div>
          </ScrollView>
        )}

        {/* El indicador de carga ahora está dentro del ScrollView, justo debajo del FileUploader */}

        {!loadFileVisible && (
          <DataGrid className='grid' dataSource={customDataSrc} ref={dataGridRef} onExporting={onExporting} hoverStateEnabled={true} height='100%' width='100%' columnAutoWidth={true} repaintChangesOnly={true} highlightChanges={true} rowAlternationEnabled={true} showColumnLines={true}>
            <SearchPanel visible={true} />
            <Scrolling columnRenderingMode='standard' useNative={true} scrollByContent={true} showScrollbar='always' />
            <Paging enabled={true} />
            <Export enabled={true} />
            <Pager visible={true} allowedPageSizes={[20, 50, 100, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
            <Editing mode='row' allowUpdating={true} allowAdding={true} allowDeleting={true} selectTextOnEditStart={true} texts={{ saveRowChanges: 'Guardar', cancelRowChanges: 'Cancelar', deleteRow: 'Eliminar', addRow: 'Agregar' }} />
            <Toolbar>
              <Item location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button
                  id='attachMult'
                  icon={LoadTarifas}
                  text='Cargar tarifas masivamente'
                  stylingMode='text'
                  onClick={() => {
                    setLoadFileVisible(true);
                  }}
                />
                <Tooltip target={'#attachMult'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Cargar tarifas masivamente
                </Tooltip>
              </Item>{' '}
              <Item name='searchPanel' />
              <Item name='addRowButton' />
              <Item name='exportButton' />
              <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button id='MassiveChange' icon={billingPrices} text='Modificar tarifas' stylingMode='text' onClick={handleMassiveChange} />
                <Tooltip target={'#MassiveChange'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Modificar masivamente las tarifas existentes
                </Tooltip>
              </Item>
              <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button id='RefrecarResultTarifas' icon='refresh' text='Refresh' stylingMode='text' onClick={handleUpdateData} />
                <Tooltip target={'#RefrecarResultTarifas'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Refrescar datos
                </Tooltip>
              </Item>
              <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button id='DeleteAllTarifas' icon='trash' text='Eliminar todas' stylingMode='text' onClick={handleDeleteAllTarifas} style={{ color: '#d9534f' }} />
                <Tooltip target={'#DeleteAllTarifas'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Eliminar todas las tarifas de este contrato
                </Tooltip>
              </Item>
            </Toolbar>
            <Column dataField='Tf_Es_Id' caption='Id' width={70} alignment='center' />
            <Column dataField='Tf_Es_Id' caption='Estudio' minWidth={250}>
              <Lookup dataSource={estudios} displayExpr={(item) => `${item.Es_Codigo} - ${item.Es_Nombre}`} valueExpr='Es_Id' />
            </Column>
            <Column dataField='Tf_Valor' format='currency' caption='Precio' dataType='number' width={150} alignment='center' />{' '}
            <Column type='buttons' caption={'ACCIÓN'} width={150}>
              <Butt name='edit' />
              <Butt name='delete' />
            </Column>
          </DataGrid>
        )}
      </Popup>
      <Popup title='Modificar tarifas masivamente' fullScreen={isMedium || isSmall || isXSmall} width={450} height={'auto'} visible={modalMassiveChangeVisible} onHiding={onCancelModalMassiveChangeClick} onHidden={onCancelModalMassiveChangeClick} showCloseButton={true}>
        {' '}
        <Form formData={massiveChange} ref={frmMassiveRef}>
          <SimpleItem
            isRequired
            dataField='Type'
            editorType='dxSelectBox'
            editorOptions={{
              showClearButton: true,
              items: [
                { label: 'Incremento en porcentaje', value: typeMassiveChange.increment_percentage },
                { label: 'Decremento en porcentaje', value: typeMassiveChange.decrement_percentage },
                { label: 'Incremento valor fijo', value: typeMassiveChange.increment_value },
                { label: 'Decremento valor fijo', value: typeMassiveChange.decrement_value },
              ],
              displayExpr: 'label',
              valueExpr: 'value',
            }}>
            <Label text='Tipo de modificación' />
          </SimpleItem>
          <SimpleItem
            isRequired
            dataField='Value'
            editorType='dxNumberBox'
            editorOptions={{
              min: 0,
              step: 0.01,
            }}>
            <Label text='Valor' />
          </SimpleItem>
        </Form>
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <Button text='Cancelar' onClick={onCancelModalMassiveChangeClick} />
          <Button text='Aplicar cambios' type='default' onClick={handleMassiveChangeConfirm} />
        </div>
      </Popup>
    </div>
  );
};
